import { base64Decode } from '@canvas/encoding';

import { type SocketMachineContext } from '../../../network-state-machine-context';
import { type AuthenticatedEvent } from '../../../network-state-machine-events';

/**
 * Decodes a JWT payload without verifying the signature.
 * Returns null if the token is malformed or unparseable.
 */
function decodeJwtPayloadUnsafe(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    return JSON.parse(base64Decode(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export async function authenticatingAsync<
  C extends Pick<SocketMachineContext, 'accessTokenSupplier' | 'debounce'>
>(ctx: C, _signal: AbortSignal): Promise<AuthenticatedEvent> {
  const accessToken = await ctx.accessTokenSupplier();

  // CNS-28626: Validate the token has a non-empty identity claim before proceeding.
  // If the client's auth session degraded during idle, accessTokenSupplier() may return
  // a structurally valid JWT whose sub/accountId payload is empty. Failing here (in
  // connecting:authenticating) causes the state machine to retry via the error path,
  // giving the auth provider a chance to restore the session, rather than silently
  // propagating a degraded token into the transport where it causes 401s on step writes.
  const payload = decodeJwtPayloadUnsafe(accessToken);
  const identityClaim = payload?.sub || payload?.accountId;
  if (!identityClaim) {
    throw new Error(
      'accessTokenSupplier returned a token with empty identity claim (sub/accountId) — forcing re-authentication'
    );
  }

  return {
    type: 'authenticated',
    accessToken,
  };
}
